import { DeploymentUnitOutlined, ShopOutlined } from "@ant-design/icons";
import { Alert, Col, Divider, Row, Skeleton } from "antd";
import type { FC } from "react";

import { PageLayout, PageTitle } from "../../../../components/AppLayout/components/Content/components";
import { Palette } from "../../../../core/styles/palette";
import { AcceptedCardTypesCard } from "../components/AcceptedCardTypesCard";
import { BaseStatisticCard } from "../components/BaseStatisticCard";
import { DataGroupInfoCard } from "../components/DataGroupInfoCard";
import { QuickLinksCard } from "../components/QuickLinksCard";
import { RecentActivityTimeline } from "../components/RecentActivityTimeline";
import { RecentCampaignsCard } from "../components/RecentCampaignsCard";
import { RecentJobExecutionsCard } from "../components/RecentJobExecutionsCard";
import { SystemHealthCard } from "../components/SystemHealthCard";
import { TerminalStatusCard } from "../components/TerminalStatusCard";
import { WeeklyCampaignActivityCard } from "../components/WeeklyCampaignActivityCard";
import { WelcomeCard } from "../components/WelcomeCard";
import { useGetDashboardQuery } from "../services/server/DashboardApi";

/**
 * DashboardPage - Main dashboard with improved layout and visualizations
 *
 * Features:
 * - Personalized welcome section
 * - Animated statistics with CountUp
 * - Interactive charts (Column, Pie)
 * - Recent campaigns grid with pagination
 * - Auto-refresh every 60 seconds
 */
export const DashboardPage: FC = () => {
    const { data, isLoading, isError, error, refetch } = useGetDashboardQuery(undefined, {
        pollingInterval: 60000, // Auto-refresh every 60 seconds
    });

    if (isLoading) {
        return (
            <PageLayout name="dashboard_page" style={{ backgroundColor: "#F4F4F5", minHeight: "100vh", padding: "24px" }}>
                <PageTitle translationKey="menu.dashboard" />
                <Divider titlePlacement="end" plain>
                    <span style={{ textTransform: "capitalize" }}>APA Dashboard</span>
                </Divider>
                <Skeleton active paragraph={{ rows: 10 }} />
            </PageLayout>
        );
    }

    if (isError || !data) {
        return (
            <PageLayout name="dashboard_page" style={{ backgroundColor: "#F4F4F5", minHeight: "100vh", padding: "24px" }}>
                <PageTitle translationKey="menu.dashboard" />
                <Divider titlePlacement="end" plain>
                    <span style={{ textTransform: "capitalize" }}>APA Dashboard</span>
                </Divider>
                <Alert
                    title="Error Loading Dashboard"
                    description={error ? JSON.stringify(error) : "Failed to load dashboard data"}
                    type="error"
                    showIcon
                    action={
                        <button
                            type="button"
                            onClick={() => refetch()}
                            style={{
                                marginLeft: 16,
                                background: "none",
                                border: "none",
                                color: "#1890ff",
                                cursor: "pointer",
                                textDecoration: "underline",
                            }}
                        >
                            Retry
                        </button>
                    }
                />
            </PageLayout>
        );
    }

    return (
        <PageLayout name="dashboard_page" style={{ backgroundColor: "#F4F4F5", minHeight: "100vh", padding: "24px" }}>
            <PageTitle translationKey="menu.dashboard" />
            <Divider titlePlacement="end" plain>
                <span style={{ textTransform: "capitalize" }}>APA Dashboard</span>
            </Divider>

            <Row gutter={[16, 16]}>
                {/* Main Content Column */}
                <Col xs={24} lg={18}>
                    {/* Hero Section with Welcome Card and Quick Stats */}
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                        <Col xs={24} lg={18}>
                            <WelcomeCard
                                applicationInfo={data.applicationInfo}
                                campaignMetrics={data.campaignMetrics}
                                orgUnitMetrics={data.orgUnitMetrics}
                                terminalMetrics={data.terminalMetrics}
                            />
                        </Col>
                        <Col xs={24} lg={6}>
                            <Row gutter={[16, 16]} style={{ height: "100%" }}>
                                <Col xs={12} lg={24} style={{ display: "flex" }}>
                                    <BaseStatisticCard
                                        value={data.orgUnitMetrics.totalSitesCount}
                                        label="Sites"
                                        icon={<ShopOutlined />}
                                        color={Palette.DarkGreen}
                                        style={{ flex: 1 }}
                                    />
                                </Col>
                                <Col xs={12} lg={24} style={{ display: "flex" }}>
                                    <BaseStatisticCard
                                        value={data.terminalMetrics.activeTerminalsCount}
                                        label="Terminals"
                                        icon={<DeploymentUnitOutlined />}
                                        color={Palette.HeroGreen}
                                        style={{ flex: 1 }}
                                    />
                                </Col>
                            </Row>
                        </Col>
                    </Row>

                    {/* Charts Row - Weekly Activity and Terminal Status */}
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                        <Col xs={24} lg={12}>
                            <WeeklyCampaignActivityCard data={data.weeklyCampaignActivity} />
                        </Col>
                        <Col xs={24} lg={12}>
                            <TerminalStatusCard data={data.terminalStatusData} />
                        </Col>
                    </Row>

                    {/* Charts Row - Accepted Card Types, System Health and Recent Job Executions */}
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                        <Col xs={24} lg={14}>
                            <AcceptedCardTypesCard data={data.acceptedCardTypes} />
                        </Col>
                        <Col xs={24} lg={10}>
                            <Row gutter={[16, 16]}>
                                <Col span={24}>
                                    <SystemHealthCard health={data.systemHealth} />
                                </Col>
                                <Col span={24}>
                                    <RecentJobExecutionsCard jobExecutions={data.systemHealth.jobExecutions} />
                                </Col>
                            </Row>
                        </Col>
                    </Row>

                    {/* Recent Campaigns Grid */}
                    <Row gutter={[16, 16]}>
                        <Col xs={24}>
                            <RecentCampaignsCard campaigns={data.campaignMetrics.recentCampaigns} loading={isLoading} error={error} />
                        </Col>
                    </Row>
                </Col>

                {/* Sidebar Column */}
                <Col xs={24} lg={6}>
                    <Row gutter={[16, 16]}>
                        {/* Data Group Info */}
                        <Col span={24}>
                            <DataGroupInfoCard dataGroupInfo={data.dataGroupInfo} />
                        </Col>

                        {/* Quick Links */}
                        <Col span={24}>
                            <QuickLinksCard />
                        </Col>

                        {/* Recent Activity Timeline */}
                        <Col span={24}>
                            <RecentActivityTimeline activity={data.recentActivity} />
                        </Col>
                    </Row>
                </Col>
            </Row>
        </PageLayout>
    );
};
